import { useActiveCompetition } from "@/features/predictions/hooks/useActiveCompetition";
import type { ScoringRule } from "@/features/predictions/types/competition.type";
import { deriveScoringRules } from "@/features/predictions/utils/deriveScoringRules";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";

const formatRowPoints = (points: number, withPlus = false): string =>
  `${withPlus ? "+" : ""}${points} pt${points > 1 ? "s" : ""}`;

// Reformulation of the first footnote, kept as a fixed row in this compact
// view (the dialog renders it as a footnote instead).
const NO_POINTS_ROW = { label: "Mauvais résultat ou pas de pronostic", points: "0 pt" };

const ScoreRow = ({ label, points }: { label: string, points: string }) => (
  <li className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-4 py-2.5 dark:bg-gray-700/60">
    <span className="text-sm text-gray-800 dark:text-gray-100">{label}</span>
    <span className="shrink-0 text-sm font-semibold text-gray-900 dark:text-white">{points}</span>
  </li>
);

const BonusRow = ({ rule }: { rule: ScoringRule }) => (
  <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
    <span className="text-sm text-gray-800 dark:text-gray-100">{rule.label}</span>
    <span className="shrink-0 text-sm font-semibold text-amber-700 dark:text-amber-300">
      {rule.points !== undefined ? formatRowPoints(rule.points, true) : ""}
    </span>
  </div>
);

// Compact rendering of the same content as RulesDialog (single wording, same
// source — API scoring_rules or local fallback). Point-less rules (the
// tiebreaker) don't fit the points/label rows and stay dialog-only.
export const RulesStep = () => {
  const { data: competition } = useActiveCompetition();
  const { sections, examples, footnotes } = deriveScoringRules(competition?.scoringRules ?? null);

  const baseRules = sections.find((section) => section.key === "base")?.rules ?? [];
  const bonusRules = (sections.find((section) => section.key === "bonus")?.rules ?? [])
    .filter((rule) => rule.points !== undefined);

  return (
    <div className="space-y-5 text-left">
      <div className="space-y-2 text-center">
        <H2>Le barème de points</H2>
        <P color="muted" size="sm">
          Voici comment tes pronostics sont notés.
        </P>
      </div>

      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Points de base
        </p>
        <ul className="space-y-1.5">
          {baseRules.map((rule) => (
            <ScoreRow
              key={rule.key}
              label={rule.label}
              points={rule.points !== undefined ? formatRowPoints(rule.points) : ""}
            />
          ))}
          <ScoreRow label={NO_POINTS_ROW.label} points={NO_POINTS_ROW.points} />
        </ul>
      </section>

      {bonusRules.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Bonus phase finale
          </p>
          <div className="space-y-1.5">
            {bonusRules.map((rule) => (
              <BonusRow key={rule.key} rule={rule} />
            ))}
          </div>
        </section>
      )}

      {examples.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Exemples
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-700/60 dark:text-gray-400">
                <tr>
                  <th scope="col" className="whitespace-nowrap px-3 py-2 text-left font-medium">Match</th>
                  <th scope="col" className="whitespace-nowrap px-3 py-2 text-left font-medium">Pronostic</th>
                  <th scope="col" className="whitespace-nowrap px-3 py-2 text-left font-medium">Détail</th>
                  <th scope="col" className="whitespace-nowrap px-3 py-2 text-right font-medium">Points</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {examples.map((example) => (
                  <tr key={`${example.match}-${example.prediction}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-200">
                      {example.match}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-200">
                      {example.prediction}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-400">
                      {example.detail}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">
                      {example.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The draw example carries an asterisk: without its resolution the
              note used to be orphaned in the onboarding. */}
          {footnotes.length > 1 && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              {footnotes[footnotes.length - 1]}
            </p>
          )}
        </section>
      )}
    </div>
  );
};

RulesStep.displayName = "RulesStep";
